/**
 * Horse Requests Manager Component
 * Shows managers pending horse requests and allows them to assign horses
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Badge,
  Collapse,
  LinearProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import { HorseRequest, HorseAssignment, Player } from "./types";
import HorseAssignmentModal from "./HorseAssignmentModal";

interface HorseAssignmentWithColor extends HorseAssignment {
  horse_color?: string;
}

interface HorseRequestsManagerProps {
  devPort: string;
  clubId: number;
  eventType: "games" | "lessons" | "tournaments";
  eventId: string | number;
  managerMode: number;
  updatePlayerAssignments?: (playerId: string | number, assignments: HorseAssignmentWithColor[]) => void;
  refreshTrigger?: number; // External trigger to refresh (increment to trigger)
  // External assignments from parent (player table) - used to sync state without API call
  externalAssignments?: HorseAssignment[]; // Array of all assignments from parent
}

interface RequestWithPlayer extends HorseRequest {
  player_name?: string;
}

function HorseRequestsManager({
  devPort,
  clubId,
  eventType,
  eventId,
  managerMode,
  updatePlayerAssignments,
  refreshTrigger,
  externalAssignments,
}: HorseRequestsManagerProps) {
  const [requests, setRequests] = useState<RequestWithPlayer[]>([]);
  const [assignments, setAssignments] = useState<HorseAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<RequestWithPlayer | null>(null);

  const isManager = managerMode === -1;
  const isLesson = eventType === "lessons";

  // Load data
  const loadData = useCallback(() => {
    setLoading(true);

    Promise.all([
      fetch(
        `${devPort}horses/requests/${clubId}/${eventType}/${eventId}`
      ).then((r) => r.json()),
      fetch(
        `${devPort}horses/assignments/${clubId}/${eventType}/${eventId}`
      ).then((r) => r.json()),
      fetch(`${devPort}player_list/${clubId}/False`).then((r) => r.json()),
    ])
      .then(([requestsData, assignmentsData, playersData]) => {
        // Format players
        const formattedPlayers: Player[] = playersData.map((p: any[]) => ({
          player_id: p[4],
          first_name: p[0],
          last_name: p[1],
        }));

        // Add player names to requests (show pending and those not fully fulfilled)
        const enrichedRequests: RequestWithPlayer[] = (
          Array.isArray(requestsData) ? requestsData : []
        )
          .filter((r: HorseRequest) => r.status === "pending" || r.status !== "cancelled")
          .map((r: HorseRequest) => {
            const player = formattedPlayers.find(
              (p) => String(p.player_id) === String(r.player_id)
            );
            return {
              ...r,
              player_name: player
                ? `${player.first_name} ${player.last_name}`
                : `Player ${r.player_id}`,
            };
          });
        setRequests(enrichedRequests);

        setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [devPort, clubId, eventType, eventId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh when external trigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      loadData();
    }
  }, [refreshTrigger, loadData]);

  // Sync assignments from parent (player table) without API call
  // Merge external assignments with existing assignments instead of replacing all
  // This ensures we don't lose assignments for players not in the signup list
  useEffect(() => {
    if (externalAssignments !== undefined) {
      setAssignments((prevAssignments) => {
        // Create a map of player IDs from external assignments
        const externalPlayerIds = new Set(
          externalAssignments.map((a) => String(a.player_id))
        );

        // Keep assignments for players NOT in external list (from API, other sources)
        const otherAssignments = prevAssignments.filter(
          (a) => !externalPlayerIds.has(String(a.player_id))
        );

        // Combine: external assignments (source of truth for signup players) + other assignments
        return [...externalAssignments, ...otherAssignments];
      });
    }
    // If externalAssignments is undefined, keep using local state from loadData
    // This ensures initial API load works correctly before players are loaded
  }, [externalAssignments]);

  const handleOpenAssignment = (request: RequestWithPlayer) => {
    setSelectedRequest(request);
    setAssignmentModalOpen(true);
  };

  const getPlayerAssignments = (playerId: string) => {
    return assignments.filter((a) => String(a.player_id) === String(playerId));
  };

  const isRequestFulfilled = (request: RequestWithPlayer) => {
    const playerAssignments = getPlayerAssignments(request.player_id);
    const assignedChukkers = playerAssignments.reduce(
      (sum, a) => sum + a.chukkers_assigned,
      0
    );
    return assignedChukkers >= request.chukkers_requested;
  };

  // Only show if manager and there are pending requests
  if (!isManager || loading) {
    return loading && isManager ? <LinearProgress /> : null;
  }

  if (requests.length === 0) {
    return null;
  }

  const pendingCount = requests.filter((r) => !isRequestFulfilled(r)).length;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setExpanded(!expanded)}
            sx={{ cursor: "pointer" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Badge badgeContent={pendingCount} color="warning">
                <AssignmentIcon />
              </Badge>
              <Typography variant="subtitle1" fontWeight="bold">
                Horse Requests
              </Typography>
            </Stack>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>

          <Collapse in={expanded}>
            {pendingCount === 0 ? (
              <Alert severity="success" variant="outlined">
                All horse requests have been fulfilled!
              </Alert>
            ) : (
              <List dense>
                {requests.map((request) => {
                  const fulfilled = isRequestFulfilled(request);
                  const playerAssignments = getPlayerAssignments(request.player_id);
                  const assignedChukkers = playerAssignments.reduce(
                    (sum, a) => sum + a.chukkers_assigned,
                    0
                  );

                  return (
                    <React.Fragment key={request.id}>
                      <ListItem
                        sx={{
                          bgcolor: fulfilled ? "success.light" : "warning.light",
                          borderRadius: 1,
                          mb: 0.5,
                          opacity: fulfilled ? 0.7 : 1,
                        }}
                      >
                        <ListItemAvatar>
                          <PersonIcon />
                        </ListItemAvatar>
                        <ListItemText
                          primary={request.player_name}
                          secondary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                size="small"
                                label={
                                  isLesson
                                    ? (fulfilled ? "Horse assigned" : "Needs horse")
                                    : (fulfilled 
                                        ? `All ${request.chukkers_requested} assigned` 
                                        : `${assignedChukkers}/${request.chukkers_requested} assigned`)
                                }
                                color={fulfilled ? "success" : "info"}//(assignedChukkers > 0 ? "info" : "warning")}
                                variant={fulfilled ? "filled" : "outlined"}
                              />
                            </Stack>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            variant={fulfilled ? "outlined" : "contained"}
                            onClick={() => handleOpenAssignment(request)}
                          >
                            {fulfilled ? "View" : "Assign"}
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Collapse>
        </Stack>
      </CardContent>

      {/* Assignment Modal */}
      {selectedRequest && (
        <HorseAssignmentModal
          open={assignmentModalOpen}
          onClose={() => {
            setAssignmentModalOpen(false);
            setSelectedRequest(null);
          }}
          devPort={devPort}
          clubId={clubId}
          eventType={eventType}
          eventId={eventId}
          playerId={selectedRequest.player_id}
          playerName={selectedRequest.player_name || ""}
          maxChukkers={selectedRequest.chukkers_requested}
          onAssignmentsChange={(newAssignments) => {
            // Update local assignments state for this component
            setAssignments((prev) => {
              // Remove old assignments for this player, add new ones
              const otherAssignments = prev.filter(
                (a) => String(a.player_id) !== String(selectedRequest.player_id)
              );
              return [...otherAssignments, ...newAssignments];
            });
            // Update parent state (signup table) without re-fetching
            if (updatePlayerAssignments) {
              updatePlayerAssignments(selectedRequest.player_id, newAssignments);
            }
          }}
        />
      )}
    </Card>
  );
}

export default HorseRequestsManager;

